<template>
  <!-- The guardrails, shown rather than told: the admin-only Auto-approve setting turns on, the
       chat confirms "Auto mode on · Sends to clients still ask", and a proposal send still stops
       for approval. Strings are the app's (askAiAutoApprove*, askAiAutoModeOn,
       askAiAutoModeSendsStillAsk, askAiConfirmAction*); the proposal is sample data. Plays once
       in view; resting styles are the final frame. With `scene: "token"` the band shows the
       personal access token dialog instead (StoryTokenScene), and with `scene: "stage"` one
       StoryStage screen (`stage`) resting on one `state`. -->
  <section class="sgr">
    <div class="sgr-shell sgr-grid">
      <StoryTokenScene v-if="chapter.scene === 'token'" class="sgr-scene-slot" />
      <LazyStoryStage v-else-if="chapter.scene === 'stage'" class="sgr-scene-slot" :screen="chapter.stage" :state="chapter.state" :label="chapter.stageLabel" />
      <div v-else ref="rootEl" class="sgr-scene" :class="{ 'is-run': running || armed, 'is-armed': armed }" aria-hidden="true">
        <div class="sgr-window">
          <div class="sgr-setting sgr-in" style="--d: 0s">
            <span class="sgr-setting-copy">
              <b>Auto-approve actions</b>
              <small>AI AutoPilot creates, updates and deletes records in this workspace without stopping to ask. Sends to clients always ask, in every mode.</small>
            </span>
            <span class="sgr-switch"><i /></span>
          </div>
          <p class="sgr-admin sgr-in" style="--d: 0.25s">Only a workspace admin can change this setting.</p>

          <div class="sgr-mode sgr-in" style="--d: 1.3s">
            <span class="sgr-dot" />
            <b>Auto mode on</b>
            <span>· Sends to clients still ask</span>
          </div>

          <div class="sgr-confirm sgr-in" style="--d: 1.9s">
            <div class="sgr-confirm-head">
              <span class="sgr-hand"><svg viewBox="0 0 24 24"><path :d="hand" /></svg></span>
              <b>Confirm before this runs</b>
            </div>
            <p>Send proposal P-2041 to Lena Park · lena@parkdesign.test</p>
            <div class="sgr-buttons">
              <span class="sgr-btn-quiet">Cancel</span>
              <span class="sgr-btn">Confirm and run</span>
            </div>
          </div>
        </div>
      </div>

      <div class="sgr-copy">
        <h2>{{ chapter.title }}</h2>
        <p class="sgr-intro">{{ chapter.intro }}</p>
        <dl class="sgr-rules">
          <div v-for="rule in chapter.rules" :key="rule.title">
            <dt>{{ rule.title }}</dt>
            <dd>{{ rule.copy }}</dd>
          </div>
        </dl>
      </div>
    </div>
  </section>
</template>

<script setup>
defineProps({
  chapter: { type: Object, required: true },
});
const rootEl = ref(null);
const { running, armed } = useMotionInView(rootEl, { threshold: 0.4 });
const hand = "M23 5.5V20c0 2.2-1.8 4-4 4h-7.3c-1.08 0-2.1-.43-2.85-1.19L1 14.83s1.26-1.23 1.3-1.25c.22-.19.49-.29.79-.29.22 0 .42.06.6.16.04.01 4.31 2.46 4.31 2.46V4c0-.83.67-1.5 1.5-1.5S11 3.17 11 4v7h1V1.5c0-.83.67-1.5 1.5-1.5S15 .67 15 1.5V11h1V2.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5V11h1V5.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5z";
</script>

<style scoped>
.sgr { background: #0b1220; padding: 6.5rem 0; color: #e2e8f0; }
.sgr-shell { width: min(100% - 3rem, var(--story-max, 84rem)); margin: 0 auto; }
.sgr-grid { display: grid; gap: 2.5rem; align-items: center; }
@media (min-width: 1024px) { .sgr-grid { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 4.5rem; } }
.sgr-copy h2 { color: #f8fafc; font-size: clamp(2rem, 4vw, 3.25rem); font-weight: 880; line-height: 1.04; text-wrap: balance; }
.sgr-intro { margin-top: 1rem; color: #cbd5e1; font-size: 1rem; line-height: 1.7; }
.sgr-rules { display: grid; gap: 0; margin-top: 1.75rem; border-top: 1px solid #1e293b; }
.sgr-rules > div { display: grid; gap: 0.3rem; border-bottom: 1px solid #1e293b; padding: 1rem 0; }
@media (min-width: 640px) { .sgr-rules > div { grid-template-columns: 12rem 1fr; gap: 1.5rem; } }
.sgr-rules dt { color: #f8fafc; font-weight: 800; }
.sgr-rules dd { color: #cbd5e1; font-size: 0.95rem; line-height: 1.6; }

/* The app window, always in the app's dark theme on this dark band. */
.sgr-scene {
  border-radius: 14px;
  background:
    radial-gradient(circle, rgb(255 255 255 / 0.1) 0.8px, transparent 1.3px) 7px 7px / 14px 14px,
    radial-gradient(120% 120% at 50% 40%, rgb(27 167 206 / 0.18), rgb(27 167 206 / 0) 70%);
  padding: 2rem;
}
.sgr-window {
  display: grid;
  gap: 0.75rem;
  max-width: 460px;
  min-height: 360px;
  margin: 0 auto;
  border: 1px solid rgb(255 255 255 / 0.13);
  border-radius: 10px;
  background: #1c1c1c;
  padding: 1rem;
  color: #ececec;
  font-family: "Space Grotesk", Inter, ui-sans-serif, system-ui, sans-serif;
  font-size: 13px;
  line-height: 1.45;
  align-content: start;
  box-shadow: 0 24px 60px -20px rgb(0 0 0 / 0.8);
}
.sgr-setting { display: flex; gap: 1rem; align-items: flex-start; border: 1px solid rgb(255 255 255 / 0.13); border-radius: 10px; background: #262626; padding: 0.8rem 0.9rem; }
.sgr-setting-copy { display: grid; gap: 0.2rem; }
.sgr-setting b { font-weight: 600; }
.sgr-setting small { color: #a8a8a8; font-size: 12px; }
.sgr-switch { position: relative; flex: none; width: 38px; height: 22px; margin-top: 2px; border-radius: 999px; background: #ececec; }
.sgr-switch i { position: absolute; top: 3px; left: 19px; width: 16px; height: 16px; border-radius: 999px; background: #171717; }
.sgr-admin { margin: -0.25rem 0 0; color: #8a8a8a; font-size: 11.5px; }
.sgr-mode { display: flex; flex-wrap: wrap; align-items: center; gap: 0.35rem; border-radius: 6px; background: rgb(241 201 94 / 0.14); padding: 0.4rem 0.6rem; color: #f1c95e; font-size: 12px; justify-self: start; }
.sgr-mode b { font-weight: 600; }
.sgr-dot { width: 7px; height: 7px; border-radius: 2px; background: currentColor; }
.sgr-confirm { border: 1px solid rgb(255 255 255 / 0.13); border-radius: 11px; background: #262626; padding: 0.85rem 0.95rem; }
.sgr-confirm-head { display: flex; align-items: center; gap: 0.5rem; }
.sgr-confirm-head b { font-weight: 600; }
.sgr-hand { display: grid; place-items: center; width: 24px; height: 24px; border-radius: 6px; background: #333; }
.sgr-hand svg { width: 14px; height: 14px; fill: currentColor; }
.sgr-confirm p { margin: 0.5rem 0 0; color: #cfcfcf; }
.sgr-buttons { display: grid; grid-template-columns: 1fr 1fr; gap: 0.5rem; margin-top: 0.8rem; }
.sgr-btn-quiet, .sgr-btn { display: grid; place-items: center; min-height: 36px; border-radius: 7px; padding: 0.3rem 0.5rem; font-weight: 600; line-height: 1.2; text-align: center; }
.sgr-btn-quiet { border: 1px solid rgb(255 255 255 / 0.15); }
.sgr-btn { position: relative; background: #ececec; color: #171717; }
.sgr-btn::after { content: ""; position: absolute; inset: -4px; border: 1.5px solid #67e8f9; border-radius: 10px; opacity: 0; }

/* Motion: the switch flips on, then the mode line and the confirm card rise; a ring marks the
   button that is still yours to press. */
.is-run .sgr-in { animation: sgr-rise 0.55s cubic-bezier(0.33, 1, 0.68, 1) var(--d) both; }
.is-run .sgr-switch { animation: sgr-track 0.3s ease 0.8s both; }
.is-run .sgr-switch i { animation: sgr-knob 0.3s ease 0.8s both; }
.is-run .sgr-btn::after { animation: sgr-ring 1.4s ease-out 2.5s 2 both; }
.is-armed * { animation-play-state: paused !important; }
@keyframes sgr-rise { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
@keyframes sgr-track { from { background: #4a4a4a; } to { background: #ececec; } }
@keyframes sgr-knob { from { transform: translateX(-16px); background: #a8a8a8; } to { transform: none; background: #171717; } }
@keyframes sgr-ring { 0% { opacity: 0; transform: scale(0.96); } 40% { opacity: 1; } 100% { opacity: 0; transform: scale(1.06); } }
@media (max-width: 1023px) { .sgr-scene, .sgr-scene-slot { order: 2; } }
@media (max-width: 639px) {
  .sgr { padding: 4.5rem 0; }
  .sgr-shell { width: min(100% - 2rem, var(--story-max, 84rem)); }
  .sgr-scene { padding: 0.9rem; }
  .sgr-window { min-height: 0; }
}
@media (prefers-reduced-motion: reduce) { .sgr-scene * { animation: none !important; } }
</style>
